<template>
  <div class="stream-player-container">
    <video
      ref="videoElement"
      :width="width"
      :height="height"
      :controls="controls"
      :autoplay="autoplay"
      :muted="autoplay || muted"
      playsinline
      webkit-playsinline
      class="stream-video"
    >
      您的浏览器不支持视频播放
    </video>
    <div v-if="loading" class="loading-overlay">
      <van-loading type="spinner" color="#1989fa">加载中...</van-loading>
    </div>
    <div v-if="error" class="error-overlay">
      <van-icon name="warning-o" size="24" color="#ee0a24" />
      <p>{{ error }}</p>
      <van-button size="small" type="primary" @click="retry">重试</van-button>
    </div>
    <div v-if="showProtocolInfo" class="protocol-info">
      <span class="protocol-badge">{{ currentProtocol }}</span>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import flvjs from 'flv.js'
import Hls from 'hls.js'

export default {
  name: 'StreamPlayer',
  props: {
    // 视频流地址
    src: {
      type: String,
      required: true
    },
    // 视频宽度
    width: {
      type: [String, Number],
      default: '100%'
    },
    // 视频高度
    height: {
      type: [String, Number],
      default: 'auto'
    },
    // 是否显示控制条
    controls: {
      type: Boolean,
      default: true
    },
    // 是否自动播放
    autoplay: {
      type: Boolean,
      default: false
    },
    // 是否静音
    muted: {
      type: Boolean,
      default: false
    },
    // 是否显示协议信息
    showProtocolInfo: {
      type: Boolean,
      default: false
    },
    // FLV配置选项
    flvConfig: {
      type: Object,
      default: () => ({
        enableWorker: false,
        enableStashBuffer: true,
        stashInitialSize: 128,
        isLive: true,
        lazyLoad: true,
        lazyLoadMaxDuration: 3 * 60,
        lazyLoadRecoverDuration: 30,
        autoCleanupSourceBuffer: true,
        autoCleanupMaxBackwardDuration: 3 * 60,
        autoCleanupMinBackwardDuration: 2 * 60,
        fixAudioTimestampGap: true,
        seekType: 'range',
        reuseRedirectedURL: true
      })
    },
    // HLS配置选项
    hlsConfig: {
      type: Object,
      default: () => ({
        debug: false,
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90
      })
    }
  },
  emits: ['loadstart', 'canplay', 'play', 'pause', 'ended', 'error', 'protocol-detected'],
  setup(props, { emit }) {
    const videoElement = ref(null)
    const flvPlayer = ref(null)
    const hlsPlayer = ref(null)
    const loading = ref(false)
    const error = ref('')
    const currentProtocol = ref('')
    const loadingTimeout = ref(null)

    // 检测流协议类型
    const detectProtocol = (url) => {
      if (!url) return 'unknown'
      
      const urlLower = url.toLowerCase()
      
      // 优先检查文件扩展名，避免域名中的关键字干扰
      if (urlLower.includes('.m3u8')) {
        return 'hls'
      } else if (urlLower.includes('.flv')) {
        return 'flv'
      } else if (urlLower.includes('.mp4')) {
        return 'mp4'
      } else if (urlLower.includes('rtmp://')) {
        return 'rtmp'
      } else if (urlLower.includes('hls')) {
        return 'hls'
      } else if (urlLower.includes('flv')) {
        return 'flv'
      }
      
      // 默认尝试HLS
      return 'hls'
    }

    // 初始化FLV播放器
    const initFlvPlayer = () => {
      if (!flvjs.isSupported()) {
        error.value = '当前浏览器不支持FLV播放'
        return false
      }

      try {
        const mediaDataSource = {
          type: 'flv',
          url: props.src,
          isLive: true,
          cors: true,
          withCredentials: false,
          hasAudio: true,
          hasVideo: true
        }
        
        // 参考SRS video_player.html的简化配置，与工作的实现保持一致
        const config = {
          enableWorker: false,
          enableStashBuffer: false,  // 关键：与SRS player保持一致
          isLive: true,  // 直播流标识
          lazyLoad: false,
          autoCleanupSourceBuffer: true,
          autoCleanupMaxBackwardDuration: 3 * 60,
          autoCleanupMinBackwardDuration: 2 * 60,
          fixAudioTimestampGap: false,
          accurateSeek: false,
          seekType: 'range',
          reuseRedirectedURL: false,
          referrerPolicy: 'no-referrer-when-downgrade',
          enableLogging: false,
          enableLogVerbose: false,
          ...props.flvConfig
        }
        
        flvPlayer.value = flvjs.createPlayer(mediaDataSource, config)
        flvPlayer.value.attachMediaElement(videoElement.value)

        // 监听FLV事件
        flvPlayer.value.on(flvjs.Events.LOADING_COMPLETE, () => {
          console.log('FLV: 加载完成')
          emit('loadstart')
        })

        // 监听脚本数据到达
        flvPlayer.value.on(flvjs.Events.SCRIPTDATA_ARRIVED, (data) => {
          console.log('FLV: 脚本数据到达', data)
        })

        // 监听统计信息更新
        flvPlayer.value.on(flvjs.Events.STATISTICS_INFO, (stats) => {
          console.log('FLV: 统计信息', stats)
          if (stats.speed > 0 && loading.value) {
            loading.value = false
            if (loadingTimeout.value) {
              clearTimeout(loadingTimeout.value)
              loadingTimeout.value = null
            }
          }
        })

        flvPlayer.value.on(flvjs.Events.MEDIA_INFO, (mediaInfo) => {
          console.log('FLV: 媒体信息', mediaInfo)
          console.log('FLV: MEDIA_INFO事件触发，设置loading=false')
          loading.value = false  // 在获取到媒体信息时取消加载状态
          // 清除加载超时
          if (loadingTimeout.value) {
            clearTimeout(loadingTimeout.value)
            loadingTimeout.value = null
          }
          emit('canplay')
        })

        // 监听网络状态变化，特别处理SRS服务器的connection: Close情况
        flvPlayer.value.on(flvjs.Events.ERROR, (errorType, errorDetail, errorInfo) => {
          loading.value = false
          // 清除加载超时
          if (loadingTimeout.value) {
            clearTimeout(loadingTimeout.value)
            loadingTimeout.value = null
          }
          console.error('FLV播放错误:', { errorType, errorDetail, errorInfo })
          
          // 检查是否是SRS服务器的连接关闭错误
          const isSRSConnectionError = errorInfo && (
            errorInfo.msg && errorInfo.msg.includes('connection') ||
            errorInfo.response && errorInfo.response.includes('Connection: close') ||
            errorDetail === flvjs.ErrorDetails.NETWORK_EXCEPTION
          )
          
          let errorMessage = 'FLV播放错误'
          let shouldRetry = false
          
          if (errorType === flvjs.ErrorTypes.NETWORK_ERROR) {
            if (isSRSConnectionError) {
              errorMessage = 'SRS服务器连接中断，正在尝试重连...'
              shouldRetry = true
            } else {
              errorMessage = 'FLV网络连接错误，请检查视频流地址'
            }
          } else if (errorType === flvjs.ErrorTypes.MEDIA_ERROR) {
            errorMessage = 'FLV媒体解码错误，请检查视频流格式'
          }
          
          if (shouldRetry) {
            // 对于SRS连接错误，延迟重连
            setTimeout(() => {
              console.log('SRS连接错误，尝试重连...')
              retry()
            }, 2000)
          } else {
            error.value = errorMessage
            emit('error', { errorType, errorDetail, errorInfo, protocol: detectProtocol(props.src) || currentProtocol.value.toLowerCase() || 'flv' })
          }
        })

        // 监听元数据到达事件
        flvPlayer.value.on(flvjs.Events.METADATA_ARRIVED, (metadata) => {
          console.log('FLV: 元数据到达', metadata)
          console.log('FLV: METADATA_ARRIVED事件触发，设置loading=false')
          loading.value = false  // 在元数据到达时取消加载状态
          // 清除加载超时
          if (loadingTimeout.value) {
            clearTimeout(loadingTimeout.value)
            loadingTimeout.value = null
          }
        })



        flvPlayer.value.on(flvjs.Events.ERROR, (errorType, errorDetail, errorInfo) => {
          loading.value = false
          // 清除加载超时
          if (loadingTimeout.value) {
            clearTimeout(loadingTimeout.value)
            loadingTimeout.value = null
          }
          console.error('FLV播放错误:', { errorType, errorDetail, errorInfo })
          console.error('FLV错误时的URL和协议信息:', {
            url: props.src,
            detectedProtocol: detectProtocol(props.src),
            currentProtocol: currentProtocol.value
          })
          
          let errorMessage = 'FLV播放错误'
          if (errorType === flvjs.ErrorTypes.NETWORK_ERROR) {
            errorMessage = 'FLV网络连接错误，请检查视频流地址'
          } else if (errorType === flvjs.ErrorTypes.MEDIA_ERROR) {
            errorMessage = 'FLV媒体解码错误，请检查视频流格式'
          }
          
          error.value = errorMessage
          emit('error', { errorType, errorDetail, errorInfo, protocol: 'flv' })
        })

        console.log('开始加载FLV流:', props.src)
        console.log('FLV: 设置loading=true，开始加载')
        flvPlayer.value.load()
        
        // 添加强制检查机制，防止某些流一直loading
        setTimeout(() => {
          if (loading.value && flvPlayer.value) {
            console.log('FLV: 强制检查播放状态，当前readyState:', videoElement.value && videoElement.value.readyState)
            console.log('FLV: Video元素状态详情:', {
              readyState: videoElement.value && videoElement.value.readyState,
              networkState: videoElement.value && videoElement.value.networkState,
              currentTime: videoElement.value && videoElement.value.currentTime,
              duration: videoElement.value && videoElement.value.duration,
              buffered: videoElement.value && videoElement.value.buffered && videoElement.value.buffered.length
            })
            // 检查video元素是否有数据 (readyState >= 1 表示有元数据)
            if (videoElement.value && videoElement.value.readyState >= 1) {
              console.log('FLV: Video元素已有数据，强制清除loading状态，当前loading值:', loading.value)
              loading.value = false
              console.log('FLV: 强制设置loading=false完成，新值:', loading.value)
              if (loadingTimeout.value) {
                clearTimeout(loadingTimeout.value)
                loadingTimeout.value = null
              }
              emit('canplay')
            } else {
              console.log('FLV: Video元素仍无数据，继续等待，当前loading值:', loading.value)
            }
          }
        }, 3000) // 3秒后强制检查
        
        // 添加更频繁的检查机制
        const checkInterval = setInterval(() => {
          if (!loading.value || !flvPlayer.value) {
            clearInterval(checkInterval)
            return
          }
          
          if (videoElement.value && videoElement.value.readyState >= 1) {
            console.log('FLV: 定期检查发现video已有数据，清除loading状态，当前loading值:', loading.value)
            loading.value = false
            console.log('FLV: 定期检查设置loading=false完成，新值:', loading.value)
            if (loadingTimeout.value) {
              clearTimeout(loadingTimeout.value)
              loadingTimeout.value = null
            }
            emit('canplay')
            clearInterval(checkInterval)
          }
        }, 1000) // 每秒检查一次
        
        // 10秒后清除检查
        setTimeout(() => {
          clearInterval(checkInterval)
        }, 10000)
        
        // 添加延迟控制机制，参考SRS播放器
        const delayControlInterval = setInterval(() => {
          if (!videoElement.value || !flvPlayer.value) {
            clearInterval(delayControlInterval)
            return
          }
          
          try {
            const buffered = videoElement.value.buffered
            if (buffered.length > 0) {
              const end = buffered.end(0)
              const currentTime = videoElement.value.currentTime
              const diff = end - currentTime
              
              // 如果缓冲超过1.5秒，跳到接近最新位置以减少延迟
              if (diff >= 1.5) {
                console.log('FLV: 检测到延迟累积', diff, '秒，调整播放位置')
                videoElement.value.currentTime = end - 0.1
              }
            }
          } catch (err) {
            console.warn('FLV: 延迟控制检查失败:', err)
          }
        }, 3000) // 每3秒检查一次
        
        // 30秒后清除延迟控制
        setTimeout(() => {
          clearInterval(delayControlInterval)
        }, 30000)
        
        return true
      } catch (err) {
        console.error('FLV播放器初始化失败:', err)
        error.value = `FLV初始化失败: ${err.message}`
        return false
      }
    }

    // 初始化HLS播放器
    const initHlsPlayer = () => {
      if (!Hls.isSupported()) {
        // 尝试原生HLS支持
        if (videoElement.value.canPlayType('application/vnd.apple.mpegurl')) {
          videoElement.value.src = props.src
          console.log('使用原生HLS支持')
          return true
        } else {
          error.value = '当前浏览器不支持HLS播放'
          return false
        }
      }

      try {
        hlsPlayer.value = new Hls(props.hlsConfig)
        
        hlsPlayer.value.on(Hls.Events.MEDIA_ATTACHED, () => {
          console.log('HLS: 媒体已附加')
        })

        hlsPlayer.value.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log('HLS: 清单解析完成')
          loading.value = false
          // 清除加载超时
          if (loadingTimeout.value) {
            clearTimeout(loadingTimeout.value)
            loadingTimeout.value = null
          }
          emit('canplay')
        })

        hlsPlayer.value.on(Hls.Events.ERROR, (event, data) => {
          console.error('HLS播放错误:', data)
          
          let errorMessage = 'HLS播放错误'
          if (data.fatal) {
            loading.value = false
            // 清除加载超时
            if (loadingTimeout.value) {
              clearTimeout(loadingTimeout.value)
              loadingTimeout.value = null
            }
            
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                errorMessage = 'HLS网络错误，请检查网络连接或M3U8文件是否存在'
                // 尝试恢复网络错误
                try {
                  hlsPlayer.value.startLoad()
                } catch (e) {
                  console.error('HLS网络错误恢复失败:', e)
                }
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                errorMessage = 'HLS媒体错误，尝试恢复中...'
                try {
                  hlsPlayer.value.recoverMediaError()
                  return // 不设置error，让恢复过程继续
                } catch (e) {
                  errorMessage = 'HLS媒体错误恢复失败'
                }
                break
              default:
                errorMessage = 'HLS致命错误，无法播放'
                break
            }
            
            error.value = errorMessage
            emit('error', { ...data, protocol: 'hls', errorMessage })
          } else {
            // 非致命错误，只记录日志，不停止播放
            console.warn('HLS非致命错误:', data)
          }
        })

        hlsPlayer.value.attachMedia(videoElement.value)
        hlsPlayer.value.loadSource(props.src)
        
        console.log('开始加载HLS流:', props.src)
        return true
      } catch (err) {
        console.error('HLS播放器初始化失败:', err)
        error.value = `HLS初始化失败: ${err.message}`
        return false
      }
    }

    // 初始化播放器
    const initPlayer = () => {
      if (!props.src) {
        error.value = '请提供有效的视频流地址'
        return
      }

      // 清除之前的超时
      if (loadingTimeout.value) {
        clearTimeout(loadingTimeout.value)
        loadingTimeout.value = null
      }

      console.log('initPlayer: 设置loading=true，开始初始化播放器，URL:', props.src)
      loading.value = true
      error.value = ''
      
      // 设置加载超时（30秒）
      loadingTimeout.value = setTimeout(() => {
        if (loading.value) {
          loading.value = false
          error.value = '视频加载超时，请检查网络连接或视频流地址'
          console.error('视频加载超时:', props.src)
        }
      }, 30000)
      
      const protocol = detectProtocol(props.src)
      currentProtocol.value = protocol.toUpperCase()
      emit('protocol-detected', protocol)
      
      console.log(`检测到协议: ${protocol}, URL: ${props.src}`)
      console.log('URL详细信息:', {
        original: props.src,
        lower: props.src.toLowerCase(),
        hasM3u8: props.src.toLowerCase().includes('.m3u8'),
        hasHls: props.src.toLowerCase().includes('hls'),
        detectedProtocol: protocol
      })

      let success = false
      
      if (protocol === 'flv') {
        success = initFlvPlayer()
      } else if (protocol === 'hls') {
        success = initHlsPlayer()
        // HLS协议不回退到FLV，避免格式不匹配错误
        if (!success) {
          console.error('HLS播放器初始化失败，不尝试FLV播放器')
        }
      } else if (protocol === 'mp4') {
        // 原生支持的MP4
        videoElement.value.src = props.src
        currentProtocol.value = 'MP4'
        loading.value = false
        success = true
      } else {
        // 未知协议，先尝试HLS，再尝试FLV
        console.log('未知协议，尝试HLS...')
        success = initHlsPlayer()
        if (!success) {
          // 检查是否是M3U8文件，如果是则不回退到FLV
          const isM3U8 = props.src && props.src.toLowerCase().includes('.m3u8')
          if (isM3U8) {
            console.error('M3U8文件HLS播放失败，不尝试FLV播放器')
            error.value = 'M3U8流播放失败，请检查流地址或服务器配置'
          } else {
            console.log('HLS失败，尝试FLV...')
            currentProtocol.value = 'FLV'
            success = initFlvPlayer()
          }
        }
      }

      if (!success) {
        loading.value = false
        error.value = '无法初始化播放器，请检查视频流地址和格式'
      }
    }

    // 销毁播放器
    const destroyPlayer = () => {
      // 销毁FLV播放器
      if (flvPlayer.value) {
        try {
          flvPlayer.value.pause()
          flvPlayer.value.unload()
          flvPlayer.value.detachMediaElement()
          flvPlayer.value.destroy()
          flvPlayer.value = null
        } catch (err) {
          console.error('销毁FLV播放器失败:', err)
        }
      }

      // 销毁HLS播放器
      if (hlsPlayer.value) {
        try {
          hlsPlayer.value.destroy()
          hlsPlayer.value = null
        } catch (err) {
          console.error('销毁HLS播放器失败:', err)
        }
      }

      // 清理video元素
      if (videoElement.value) {
        videoElement.value.src = ''
        videoElement.value.load()
      }
    }

    // 重试播放
    const retry = () => {
      destroyPlayer()
      setTimeout(() => {
        initPlayer()
      }, 1000)
    }

    // 播放
    const play = () => {
      if (videoElement.value) {
        videoElement.value.play().then(() => {
          emit('play')
        }).catch(err => {
          console.error('播放失败:', err)
        })
      }
    }

    // 暂停
    const pause = () => {
      if (videoElement.value) {
        videoElement.value.pause()
        emit('pause')
      }
    }

    // 监听src变化
    watch(() => props.src, (newSrc, oldSrc) => {
      if (newSrc !== oldSrc) {
        destroyPlayer()
        if (newSrc) {
          initPlayer()
        }
      }
    })

    onMounted(() => {
      // 添加video元素事件监听
      if (videoElement.value) {
        videoElement.value.addEventListener('loadstart', () => {
          console.log('Video元素开始加载')
          emit('loadstart')
        })
        
        videoElement.value.addEventListener('loadedmetadata', () => {
          console.log('Video元素元数据加载完成')
          loading.value = false
          if (loadingTimeout.value) {
            clearTimeout(loadingTimeout.value)
            loadingTimeout.value = null
          }
        })
        
        videoElement.value.addEventListener('canplay', () => {
          console.log('Video元素可以播放')
          loading.value = false
          // 清除加载超时
          if (loadingTimeout.value) {
            clearTimeout(loadingTimeout.value)
            loadingTimeout.value = null
          }
          emit('canplay')
        })
        
        videoElement.value.addEventListener('canplaythrough', () => {
          console.log('Video元素可以流畅播放')
          loading.value = false
          if (loadingTimeout.value) {
            clearTimeout(loadingTimeout.value)
            loadingTimeout.value = null
          }
        })
        
        videoElement.value.addEventListener('playing', () => {
          console.log('Video元素正在播放')
          loading.value = false
          if (loadingTimeout.value) {
            clearTimeout(loadingTimeout.value)
            loadingTimeout.value = null
          }
        })
        
        videoElement.value.addEventListener('ended', () => {
          emit('ended')
        })
        
        videoElement.value.addEventListener('error', (e) => {
          console.error('Video元素错误:', e)
          loading.value = false
          error.value = 'Video元素播放错误'
          emit('error', { error: e, protocol: currentProtocol.value.toLowerCase() })
        })
      }
      
      // 初始化播放器
      if (props.src) {
        console.log('组件挂载，准备初始化播放器，当前loading状态:', loading.value)
        initPlayer()
      }
    })

    onUnmounted(() => {
      // 清除加载超时
      if (loadingTimeout.value) {
        clearTimeout(loadingTimeout.value)
        loadingTimeout.value = null
      }
      destroyPlayer()
    })

    return {
      videoElement,
      loading,
      error,
      currentProtocol,
      retry,
      play,
      pause
    }
  }
}
</script>

<style scoped>
.stream-player-container {
  position: relative;
  display: inline-block;
  width: 100%;
}

.stream-video {
  width: 100%;
  height: auto;
  background: #000;
}

.loading-overlay,
.error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  z-index: 10;
}

.error-overlay p {
  margin: 10px 0;
  text-align: center;
  font-size: 14px;
}

.protocol-info {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 5;
}

.protocol-badge {
  background: rgba(25, 137, 250, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}
</style>